'use client';

import { type ReactNode, Component, type ErrorInfo } from 'react';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/ui/shadcn/card';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { AlertCircle, RotateCcw, Home } from 'lucide-react';
import { createLogger } from '@/shared/lib/logger';
import { formatErrorForDisplay } from '@/shared/lib/error-formatter';
import * as Sentry from '@sentry/nextjs';

const logger = createLogger('SentryErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: (error: Error, resetError: () => void) => ReactNode;
  showDialog?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  showFullStack: boolean;
}

export class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, showFullStack: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, showFullStack: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Capture exception in Sentry with React context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    // Also log to console/logger
    logger.error('Component error caught:', error.message, {
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, showFullStack: false });
  };

  toggleStackTrace = () => {
    this.setState({ showFullStack: !this.state.showFullStack });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default fallback UI
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <CardTitle className="text-screen-subtitle text-destructive">
                Something went wrong
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center mb-6">
                We&apos;re sorry, but something unexpected happened. Please try
                again.
              </p>
              <div className="space-y-3">
                <Button onClick={this.resetError} className="w-full">
                  <RotateCcw className="w-4 h-4 me-2" />
                  Try Again
                </Button>
                <Button
                  onClick={() => (window.location.href = '/')}
                  variant="outline"
                  className="w-full"
                >
                  <Home className="w-4 h-4 me-2" />
                  Go to Projects
                </Button>
              </div>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mt-6">
                  <Alert className="mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="font-mono text-card-meta">
                      <details>
                        <summary className="cursor-pointer mb-2 font-sans text-list-item-subtitle text-muted-foreground">
                          Error Details (Development Only)
                        </summary>
                        <pre className="mt-2 bg-muted p-2 rounded overflow-auto whitespace-pre-wrap">
                          {this.state.showFullStack
                            ? formatErrorForDisplay(this.state.error).fullStack
                            : formatErrorForDisplay(this.state.error)
                                .shortStack}
                        </pre>
                      </details>
                    </AlertDescription>
                  </Alert>
                  {formatErrorForDisplay(this.state.error).canShowFull && (
                    <Button
                      onClick={this.toggleStackTrace}
                      variant="ghost"
                      size="sm"
                      className="text-card-meta"
                    >
                      {this.state.showFullStack
                        ? 'Show less'
                        : 'Show full stack trace'}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
